import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Plus, ShieldAlert, Check } from "lucide-react";

const SEVERITIES = ["low", "moderate", "high", "critical"];
const CATEGORIES = ["injury", "elopement", "meltdown", "medication", "property damage", "other"];

const SEVERITY_TONE = {
  low: "bg-stone2-100 text-stone2-800",
  moderate: "bg-ochre-100 text-ochre-800",
  high: "bg-danger/10 text-danger",
  critical: "bg-danger text-white",
};

export default function IncidentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({
    booking_id: "", severity: "moderate", category: "injury",
    description: "", actions_taken: "", follow_up_required: false,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, b] = await Promise.all([api.get("/incidents"), api.get("/bookings")]);
      setItems(i.data.results);
      setBookings(b.data.results);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Please describe what happened");
    setSaving(true);
    try {
      await api.post("/incidents", {
        booking_id: form.booking_id || null,
        severity: form.severity,
        category: form.category,
        description: form.description,
        actions_taken: form.actions_taken,
        follow_up_required: form.follow_up_required,
      });
      toast.success("Incident reported");
      setShowForm(false);
      setForm({ booking_id: "", severity: "moderate", category: "injury",
        description: "", actions_taken: "", follow_up_required: false });
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  const resolve = async (id) => {
    try { await api.post(`/incidents/${id}/resolve`); toast.success("Marked resolved"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="incidents-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Safety records</span>
            <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-sage-600" /> Incident reports
            </h1>
            <p className="text-stone2-600 mt-1 text-sm">Log anything that requires attention or follow-up. Critical incidents are escalated to admins.</p>
          </div>
          <button type="button" onClick={() => setShowForm((s) => !s)} data-testid="incidents-new-btn" className="btn-accent">
            <Plus className="w-4 h-4" /> Report incident
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="paper-card p-6 mt-6 space-y-4" data-testid="incidents-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Related booking (optional)</label>
                <select value={form.booking_id} onChange={(e) => setForm((f) => ({ ...f, booking_id: e.target.value }))}
                  data-testid="incidents-booking-select"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  <option value="">— none —</option>
                  {bookings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {new Date(b.start_at).toLocaleDateString()} · {user?.role === "family" ? b.sitter_name : b.family_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Severity</label>
                <select value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                  data-testid="incidents-severity-select"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Category</label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  data-testid="incidents-category-select"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm mt-6 text-sage-900">
                <input type="checkbox" checked={form.follow_up_required}
                  onChange={(e) => setForm((f) => ({ ...f, follow_up_required: e.target.checked }))}
                  data-testid="incidents-followup-checkbox"
                  className="rounded text-sage-600 focus:ring-sage-500" />
                Follow-up required
              </label>
            </div>
            <div>
              <label className="field-label">Description</label>
              <textarea rows={4} required value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="incidents-description"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="field-label">Actions taken</label>
              <textarea rows={3} value={form.actions_taken}
                onChange={(e) => setForm((f) => ({ ...f, actions_taken: e.target.value }))}
                data-testid="incidents-actions"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} data-testid="incidents-submit" className="btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Submit report
              </button>
            </div>
          </form>
        )}

        {loading && <div className="mt-8 text-stone2-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="mt-8 paper-card p-8 text-center text-stone2-500">No incidents reported. Stay safe out there.</div>
        )}

        <ul className="mt-8 space-y-3" data-testid="incidents-list">
          {items.map((i) => (
            <li key={i.id} className="paper-card p-4" data-testid={`incident-row-${i.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${SEVERITY_TONE[i.severity]}`}>
                      {i.severity}
                    </span>
                    <span className="text-xs text-stone2-500 capitalize">{i.category}</span>
                    {i.status === "resolved" && <span className="chip"><Check className="w-3 h-3 mr-1" /> resolved</span>}
                    {i.follow_up_required && <span className="chip-ochre">follow-up</span>}
                  </div>
                  <p className="mt-2 text-sm text-sage-900 whitespace-pre-line">{i.description}</p>
                  {i.actions_taken && <p className="mt-2 text-xs text-stone2-600 italic">Actions: {i.actions_taken}</p>}
                  <p className="mt-2 text-[11px] text-stone2-500">
                    Reported by {i.reported_by_name} ({i.reported_by_role}) · {new Date(i.created_at).toLocaleString()}
                  </p>
                </div>
                {i.status !== "resolved" && (i.reported_by_user_id === user?.id || user?.role === "admin") && (
                  <button type="button" onClick={() => resolve(i.id)}
                    data-testid={`incident-resolve-${i.id}`}
                    className="btn-outline-sage text-xs">
                    <Check className="w-3.5 h-3.5" /> Mark resolved
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
